import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/ui/lib/utils";

/**
 * Minimal shape a chunked runner exposes per unit of work. Both
 * `useBatchChunkedReview` (review batches) and `useChunkedExportRun` (export
 * runs) produce states assignable to this.
 */
export interface ChunkProgressItem {
  index: number;
  count: number;
  status: "pending" | "in_progress" | "success" | "failed";
  error?: string;
  imageId?: number;
}

interface ChunkProgressListProps {
  chunks: ChunkProgressItem[];
  /** Noun for one unit of work — "chunk" for review batches, "run" for exports. */
  unit?: "chunk" | "run";
}

/**
 * Per-chunk progress bars plus the backend reason for the failed chunk. The
 * error block is rendered for single-chunk failures too, where the list above
 * it is hidden.
 */
const ChunkProgressList = ({
  chunks,
  unit = "chunk",
}: ChunkProgressListProps) => {
  const { t } = useTranslation("mlExports");
  const failed = chunks.find((c) => c.status === "failed");
  const unitLabel =
    unit === "run" ? t("chunkProgress.unitRun") : t("chunkProgress.unitChunk");

  return (
    <>
      {chunks.length > 1 && (
        <div className="w-full max-w-sm mx-auto space-y-1 mb-3">
          {chunks.map((chunk) => (
            <div key={chunk.index} className="flex items-center gap-2 text-xs">
              <span className="w-16 text-muted-foreground font-mono">
                {t("chunkProgress.position", {
                  unit: unitLabel,
                  index: chunk.index + 1,
                  total: chunks.length,
                })}
              </span>
              <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className={cn(
                    "h-full transition-all",
                    chunk.status === "success" && "bg-emerald-500 w-full",
                    chunk.status === "failed" && "bg-red-500 w-full",
                    chunk.status === "in_progress" && "bg-blue-500 w-1/2 animate-pulse",
                    chunk.status === "pending" && "w-0",
                  )}
                />
              </div>
              <span className="w-4">
                {chunk.status === "success" && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />}
                {chunk.status === "failed" && <XCircle className="h-3.5 w-3.5 text-red-500" />}
                {chunk.status === "in_progress" && <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />}
              </span>
            </div>
          ))}
        </div>
      )}

      {failed?.error && (
        <div className="w-full max-w-sm mx-auto flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/5 px-3 py-2 text-left">
          <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5" />
          <p className="text-xs text-red-600 dark:text-red-400">
            {failed.error}
            {failed.imageId != null && (
              <span className="font-mono ml-1">
                {t("chunkProgress.imageRef", { id: failed.imageId })}
              </span>
            )}
          </p>
        </div>
      )}
    </>
  );
};

export default ChunkProgressList;
